import { createSelector } from '@reduxjs/toolkit';

import { TopicsScores } from '@/domain/impact-calculate/entities/calculate.entity';
import { RootState } from '@/domain/store';

import { getScoreLetterFromScoreVenueDates } from '../impact-calculate/utils/get-score-letter-from-score-venue-dates';
import { CalculateVenue } from './entities/calculate-venue.entity';
import { EMPTY_CALCULATE_VENUE_CO2 } from './entities/calculate-venue-co2.entity';
import { CalculateVenueCo2 } from './entities/calculate-venue-co2.entity';
import {
  CalculateVenueEnv,
  EMPTY_CALCULATE_VENUE_ENV,
} from './entities/calculate-venue-env.entity';
import {
  CalculateVenueEss,
  EMPTY_CALCULATE_VENUE_ESS,
} from './entities/calculate-venue-ess.entity';

export const selectImpactCalculateVenueCo2ForProject =
  (projectId: number) =>
  (state: RootState): CalculateVenueCo2 => {
    const calculate = selectImpactCalculateVenueForProject(projectId)(state);
    if (calculate?.co2) {
      return calculate.co2;
    } else {
      return EMPTY_CALCULATE_VENUE_CO2;
    }
  };

export const selectImpactCalculateVenueEnvForProject =
  (projectId: number) =>
  (state: RootState): CalculateVenueEnv => {
    const calculate = selectImpactCalculateVenueForProject(projectId)(state);
    if (calculate?.env) {
      return calculate.env;
    } else {
      return EMPTY_CALCULATE_VENUE_ENV;
    }
  };

export const selectImpactCalculateVenueEssForProject =
  (projectId: number) =>
  (state: RootState): CalculateVenueEss => {
    const calculate = selectImpactCalculateVenueForProject(projectId)(state);
    if (calculate?.ess) {
      return calculate.ess;
    } else {
      return EMPTY_CALCULATE_VENUE_ESS;
    }
  };

export const selectImpactCalculateVenueForProject =
  (projectId: number) =>
  (state: RootState): CalculateVenue | null => {
    const calculate = state.impactCalculateVenue.byProjectId[projectId];
    if (calculate) {
      return calculate;
    } else {
      return null;
    }
  };

export const selectVenueScoresByMonth = (projectId: number) =>
  createSelector(
    [selectImpactCalculateVenueForProject(projectId)],
    (calculateVenue): Record<string, TopicsScores> => {
      const co2ByMonth = calculateVenue?.co2?.dates.byMonth;
      if (!co2ByMonth) {
        return {};
      }
      const months = Object.entries(co2ByMonth).map(([key]) => key);
      const scoresByMonth = months.reduce((acc, month) => {
        const entry = {
          co2: co2ByMonth[month].score.toLocaleString() || '-',
          env: envScoreToString(co2ByMonth[month].env),
          ess: '-',
          totalMen: co2ByMonth[month].totalMen?.toLocaleString() || '-',
          totalWomen: co2ByMonth[month].totalWomen?.toLocaleString() || '-',
          totalOther: co2ByMonth[month].totalOther?.toLocaleString() || '-',
        };
        return { ...acc, [month]: entry };
      }, {});
      return scoresByMonth;
    },
  );

function envScoreToString(
  env: { score: number; maxScore: number } | undefined,
): string {
  if (!env || env.maxScore === 0) {
    return '-';
  }
  const scoreLetter = getScoreLetterFromScoreVenueDates(env.score);
  return scoreLetter;
}

export const selectVenueScoresByDates = (projectId: number) =>
  createSelector(
    [selectImpactCalculateVenueForProject(projectId)],
    (calculateVenue): Record<number, TopicsScores> => {
      const co2ByDate = calculateVenue?.co2?.dates.byDate;
      if (!co2ByDate) {
        return {};
      }
      const datesId = Object.entries(co2ByDate).map(([key]) =>
        Number.parseInt(key),
      );
      const scoresByDates = datesId.reduce((acc, dateId) => {
        const entry = {
          co2: co2ByDate[dateId].score.toLocaleString() || '-',
          env: '-',
          ess: '-',
        };
        return { ...acc, [dateId]: entry };
      }, {});
      return scoresByDates;
    },
  );
